import { NavPublic } from "@/components/NavPublic";

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      <NavPublic />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
  <h1 className="page-title text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        
        <p className="text-sm text-gray-600 mb-8">Last updated: October 3, 2025</p>

        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Smart Care Audits ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our compliance management platform for UK care homes.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              This policy applies to information we collect through our platform and in connection with our services, and complies with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Data Controller Contact Information</h2>
            <p className="text-gray-700 leading-relaxed">
              <strong>Company:</strong> Smart Care Audits<br />
              <strong>Email:</strong> info@smartcareaudits.co.uk<br />
              <strong>Website:</strong> smartcareaudits.co.uk
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              For data protection queries, please contact us at the email address above.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.1 Personal Information</h3>
            <p className="text-gray-700 leading-relaxed">We may collect the following types of personal information:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li><strong>Account Information:</strong> Name, email address, job title, username, password</li>
              <li><strong>Professional Information:</strong> Care home details, organization affiliation, role and permissions</li>
              <li><strong>Audit Data:</strong> Audit responses, compliance records, documentation, attachments</li>
              <li><strong>Usage Data:</strong> Login times, feature usage, audit completion rates</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.2 Special Category Data</h3>
            <p className="text-gray-700 leading-relaxed">
              Our platform may process health and social care data as part of audit records. This is processed under the legal basis of:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Public interest in the area of public health and social care (Article 9(2)(h) UK GDPR)</li>
              <li>Explicit consent where required</li>
              <li>Necessary for compliance with regulatory obligations (CQC, NHS requirements)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed">We process your personal data for the following purposes:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li><strong>Service Delivery:</strong> To provide audit management and compliance tracking services</li>
              <li><strong>Account Management:</strong> To create and manage user accounts and access permissions</li>
              <li><strong>Compliance Reporting:</strong> To generate audit reports and compliance documentation</li>
              <li><strong>Communication:</strong> To send service updates, notifications about due audits, and support responses</li>
              <li><strong>Service Improvement:</strong> To analyze usage patterns and improve our platform</li>
              <li><strong>Legal Obligations:</strong> To comply with CQC, NHS, and other regulatory requirements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Legal Basis for Processing</h2>
            <p className="text-gray-700 leading-relaxed">We process your personal data under the following legal bases:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li><strong>Contract:</strong> Processing necessary to perform our services under our agreement with you</li>
              <li><strong>Legal Obligation:</strong> Compliance with regulatory requirements (CQC, NHS Digital standards)</li>
              <li><strong>Legitimate Interests:</strong> Improving our services, preventing fraud, system security</li>
              <li><strong>Consent:</strong> For marketing communications and certain data processing activities</li>
              <li><strong>Public Interest:</strong> Healthcare provision and public health monitoring</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Sharing and Recipients</h2>
            <p className="text-gray-700 leading-relaxed">We may share your information with:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li><strong>Regulatory Bodies:</strong> CQC, NHS Digital, and other healthcare regulators as required by law</li>
              <li><strong>Service Providers:</strong> Cloud hosting providers, IT support services (under strict data processing agreements)</li>
              <li><strong>Within Your Organization:</strong> Data is accessible to authorized users within your care home or parent organization based on role permissions</li>
              <li><strong>Legal Requirements:</strong> Law enforcement or regulatory authorities when legally required</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              We do not sell or rent your personal data to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Your data is stored and processed within the United Kingdom. If we need to transfer data outside the UK/EEA, we will:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Only use providers in countries with an adequacy decision, or</li>
              <li>Implement appropriate safeguards such as Standard Contractual Clauses (SCCs)</li>
              <li>Obtain your explicit consent where required</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">We retain personal data for the following periods:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li><strong>Audit Records:</strong> In accordance with NHS Digital Records Management Code of Practice and CQC requirements (typically 7-10 years)</li>
              <li><strong>User Accounts:</strong> For the duration of your subscription plus 6 years for legal compliance</li>
              <li><strong>Financial Records:</strong> 7 years minimum as required by UK tax law</li>
              <li><strong>Marketing Consent:</strong> Until consent is withdrawn</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Your Data Protection Rights</h2>
            <p className="text-gray-700 leading-relaxed">Under UK GDPR, you have the following rights:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li><strong>Right to Access:</strong> Request copies of your personal data</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your data (subject to legal retention requirements)</li>
              <li><strong>Right to Restrict Processing:</strong> Request limitation on how we use your data</li>
              <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
              <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time (where consent is the legal basis)</li>
              <li><strong>Right to Lodge a Complaint:</strong> Contact the Information Commissioner's Office (ICO) at ico.org.uk</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise these rights, please contact us at info@smartcareaudits.co.uk. We will respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Security Measures</h2>
            <p className="text-gray-700 leading-relaxed">We implement appropriate technical and organizational measures to protect your data:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li><strong>Encryption:</strong> Data in transit (TLS/SSL) and at rest (AES-256)</li>
              <li><strong>Access Controls:</strong> Role-based permissions, multi-factor authentication options</li>
              <li><strong>Audit Logging:</strong> Comprehensive activity logs for security monitoring</li>
              <li><strong>Regular Security Reviews:</strong> Vulnerability assessments and penetration testing</li>
              <li><strong>Staff Training:</strong> Regular data protection training for all personnel</li>
              <li><strong>Data Breach Procedures:</strong> Incident response plan with notification to ICO within 72 hours if required</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Cookies and Tracking</h2>
            <p className="text-gray-700 leading-relaxed">
              We use essential cookies to operate our platform (session management, authentication). We do not use marketing or tracking cookies without your explicit consent. You can manage cookie preferences through your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our platform is designed for professional use by adults (18+). We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Posting the updated policy on our platform with a new "Last Updated" date</li>
              <li>Sending an email notification to registered users for significant changes</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Continued use of our platform after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>Email:</strong> info@smartcareaudits.co.uk<br />
              <strong>Subject Line:</strong> Privacy Policy Inquiry
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              For complaints about data protection, you can also contact the UK Information Commissioner's Office:
            </p>
            <p className="text-gray-700 leading-relaxed mt-2">
              <strong>ICO Website:</strong> <a href="https://ico.org.uk" className="text-purple-600 hover:text-purple-700 underline">ico.org.uk</a><br />
              <strong>ICO Helpline:</strong> 0303 123 1113
            </p>
          </section>
        </div>
      </div>

      <footer className="bg-gray-900 text-gray-300 py-8 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-4xl mx-auto text-center text-sm">
          <p>&copy; {new Date().getFullYear()} Smart Care Audits. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
